/**
 * useVetSupply — V2.K.8
 *
 * Fetches VET and VTHO supply data from CoinGecko in parallel.
 *
 * VeChain is unique among the chains we cover : it runs a **dual-
 * token economic model** :
 *  - **VET** is the value token, capped at 86.7 B and used for
 *    staking. Holders mint NFTs via the StarGate platform (min
 *    10 000 VET) to earn VTHO rewards.
 *  - **VTHO** is the gas/utility token. Pre-Hayabusa it was generated
 *    passively from any VET holding at 0.000432 VTHO/day/VET. After
 *    the Hayabusa hardfork (Renaissance phase 2, April 2025) VTHO is
 *    generated **only** by active staking. 100 % of base
 *    transaction fees are burned (EIP-1559-style) → VTHO trajectory
 *    is deflationary as adoption grows.
 *
 * Refresh : every 5 minutes (CoinGecko polls a few times a day).
 */

import { useQuery } from '@tanstack/react-query';

export interface VetSupplyData {
  // VET ─────────────────────────────────────────────────────────────
  vetCirculating: number;
  vetTotal: number;
  vetMax: number;
  vetMintedPct: number;
  vetRemaining: number;
  // VTHO ────────────────────────────────────────────────────────────
  vthoCirculating: number;
  vthoTotal: number;
  /** Pre-Hayabusa passive generation rate (still useful as a
   *  reference — equals the daily VTHO/VET coefficient). */
  vthoLegacyDailyRate: number;
  /** Whether VTHO has a hard cap. False for VTHO — uncapped. */
  vthoUncapped: true;
  lastUpdated: string | null;
}

const VET_MAX_SUPPLY = 86_712_634_466; // VeChain Foundation hard cap.
const VTHO_LEGACY_DAILY_RATE = 0.000432; // VTHO/day/VET pre-Hayabusa.

async function fetchVetSupply(): Promise<VetSupplyData> {
  const [vetRes, vthoRes] = await Promise.all([
    fetch(
      'https://api.coingecko.com/api/v3/coins/vechain?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
    ),
    fetch(
      'https://api.coingecko.com/api/v3/coins/vethor-token?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
    ),
  ]);
  if (!vetRes.ok) throw new Error(`CoinGecko (VET) returned ${vetRes.status}`);
  if (!vthoRes.ok) throw new Error(`CoinGecko (VTHO) returned ${vthoRes.status}`);

  const vetJson = await vetRes.json();
  const vthoJson = await vthoRes.json();
  const vetMd = vetJson.market_data || {};
  const vthoMd = vthoJson.market_data || {};

  const vetCirculating = Number(vetMd.circulating_supply) || 0;
  const vetTotal = Number(vetMd.total_supply) || vetCirculating;
  const vetMax = Number(vetMd.max_supply) || VET_MAX_SUPPLY;

  const vthoCirculating = Number(vthoMd.circulating_supply) || 0;
  const vthoTotal = Number(vthoMd.total_supply) || vthoCirculating;

  return {
    vetCirculating,
    vetTotal,
    vetMax,
    vetMintedPct: vetMax > 0 ? (vetTotal / vetMax) * 100 : 0,
    vetRemaining: Math.max(0, vetMax - vetTotal),
    vthoCirculating,
    vthoTotal,
    vthoLegacyDailyRate: VTHO_LEGACY_DAILY_RATE,
    vthoUncapped: true,
    lastUpdated: vetJson.last_updated || vthoJson.last_updated || null,
  };
}

export function useVetSupply() {
  return useQuery({
    queryKey: ['vet', 'supply'],
    queryFn: fetchVetSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
